/**
 * Éléments de base et arbre de combinaisons Evolutrix.
 * Données structurées : chaque combinaison est [élément A, élément B, résultat, complexité].
 */

export const BASE_ELEMENTS = ['Terre', 'Eau', 'Air', 'Feu', 'Vie', 'Métal'] as const;
export type BaseElement = (typeof BASE_ELEMENTS)[number];

export interface ElementInfo {
  id: string;
  name: string;
  complexity: number;
}

export interface CombinationResult {
  result: string;
  complexity: number;
}

/** Combinaisons : [A, B, résultat, complexité du résultat] */
const COMBINATIONS: readonly [string, string, string, number][] = [
  ['Terre', 'Terre', 'Roche', 2],
  ['Eau', 'Eau', 'Océan', 2],
  ['Air', 'Air', 'Vent', 2],
  ['Feu', 'Feu', 'Brasier', 2],
  ['Vie', 'Vie', 'ADN', 2],
  ['Métal', 'Métal', 'Engrenage', 2],
  ['Terre', 'Eau', 'Boue', 2],
  ['Terre', 'Air', 'Poussière', 2],
  ['Terre', 'Feu', 'Lave', 2],
  ['Terre', 'Vie', 'Graine', 2],
  ['Terre', 'Métal', 'Minerai', 2],
  ['Eau', 'Air', 'Nuage', 2],
  ['Eau', 'Feu', 'Vapeur', 2],
  ['Eau', 'Vie', 'Cellule', 2],
  ['Eau', 'Métal', 'Rouille', 2],
  ['Air', 'Feu', 'Fumée', 2],
  ['Air', 'Vie', 'Spore', 2],
  ['Air', 'Métal', 'Étincelle', 2],
  ['Feu', 'Vie', 'Cendre', 2],
  ['Feu', 'Métal', 'Lingot', 2],
  ['Vie', 'Métal', 'Prothèse', 2],
  ['Roche', 'Air', 'Sable', 3],
  ['Roche', 'Eau', 'Galets', 3],
  ['Boue', 'Air', 'Argile', 3],
  ['Argile', 'Feu', 'Céramique', 4],
  ['Céramique', 'Eau', 'Jarre', 5],
  ['Nuage', 'Eau', 'Pluie', 4],
  ['Nuage', 'Vent', 'Tempête', 4],
  ['Nuage', 'Étincelle', 'Orage', 4],
  ['Vapeur', 'Air', 'Brume', 3],
  ['Brume', 'Eau', 'Brouillard', 4],
  ['Pluie', 'Terre', 'Marais', 5],
  ['Océan', 'Terre', 'Île', 3],
  ['Océan', 'Vent', 'Vagues', 4],
  ['Lave', 'Eau', 'Obsidienne', 4],
  ['Lave', 'Roche', 'Volcan', 4],
  ['Volcan', 'Terre', 'Montagne', 5],
  ['Montagne', 'Air', 'Froid', 6],
  ['Froid', 'Eau', 'Glace', 7],
  ['Nuage', 'Froid', 'Neige', 8],
  ['Neige', 'Vent', 'Blizzard', 10],
  ['Cellule', 'Cellule', 'Colonie', 4],
  ['Colonie', 'ADN', 'Organisme', 6],
  ['Organisme', 'Air', 'Animal', 7],
  ['Organisme', 'Océan', 'Poisson', 8],
  ['Spore', 'Marais', 'Champignon', 7],
  ['Animal', 'Terre', 'Mammifère', 8],
  ['Animal', 'Marais', 'Amphibien', 12],
  ['Mammifère', 'Feu', 'Humain', 9],
  ['Graine', 'Eau', 'Plante', 3],
  ['Plante', 'Terre', 'Arbre', 4],
  ['Plante', 'Air', 'Fleur', 4],
  ['Céramique', 'Brasier', 'Four', 6],
  ['Four', 'Lingot', 'Forge', 8],
  ['Forge', 'Lingot', 'Outil métallique', 10],
  ['Engrenage', 'Lingot', 'Atelier', 4],
  ['Atelier', 'Roche', 'Outil', 6],
  ['Outil', 'Arbre', 'Bûche', 10],
  ['Bûche', 'Feu', 'Feu de camp', 11],
  ['Feu de camp', 'Humain', 'Cuisine', 20],
  ['Cuisine', 'Poisson', 'Poisson grillé', 28],
  ['Cuisine', 'Fleur', 'Infusion', 24],
  ['Cuisine', 'Champignon', 'Ragoût', 27],
  ['Bûche', 'Outil', 'Planche', 16],
  ['Planche', 'Planche', 'Cabane', 32],
  ['Cabane', 'Humain', 'Village', 41],
  ['Sable', 'Feu', 'Verre', 4],
  ['Verre', 'Métal', 'Fenêtre', 5],
  ['Cabane', 'Fenêtre', 'Maison', 37],
  ['Lingot', 'Lingot', 'Alliage', 4],
  ['Alliage', 'Brasier', 'Acier', 6],
  ['Acier', 'Outil', "Hache d'acier", 12],
  ['Engrenage', 'Lingot', 'Machine', 4],
  ['Machine', 'Brasier', 'Moteur', 6],
  ['Machine', 'Vent', 'Éolienne', 6],
  ['Machine', 'Eau', 'Pompe', 5],
  ['Pompe', 'Terre', 'Irrigation', 6],
  ['Irrigation', 'Plante', 'Agriculture', 9],
  ['Village', 'Agriculture', 'Marché', 50],
  ['Marché', 'Métal', 'Monnaie', 51],
  ['Marché', 'Monnaie', 'Commerce', 101],
  ['Village', 'Commerce', 'Ville', 142],
  ['Orage', 'Machine', 'Générateur', 8],
  ['Générateur', 'Ville', 'Électricité', 150],
  ['Électricité', 'Machine', 'Ordinateur', 154],
  ['Ordinateur', 'Humain', 'Scientifique', 163],
  ['Scientifique', 'Verre', 'Laboratoire', 167],
  ['Mammifère', 'Mammifère', 'Couple', 16],
  ['Couple', 'Vie', 'Reproduction', 17],
  ['Reproduction', 'Eau', 'Spermatozoïde', 18],
  ['Reproduction', 'Terre', 'Ovule', 18],
  ['Spermatozoïde', 'Ovule', 'Cellule-œuf', 36],
  ['Cellule-œuf', 'Eau', 'Embryon', 37],
  ['Embryon', 'Vie', 'Bébé', 38],
  ['Bébé', 'Humain', 'Famille', 47],
  ['Famille', 'Village', 'Communauté', 88],
];

const combinationMap = new Map<string, CombinationResult>();
const elementsByName = new Map<string, ElementInfo>();
const recipesByResult = new Map<string, [string, string][]>();

function buildMaps(): void {
  BASE_ELEMENTS.forEach((name) => {
    elementsByName.set(name, { id: name, name, complexity: 1 });
  });
  for (const [a, b, result, complexity] of COMBINATIONS) {
    combinationMap.set(`${a},${b}`, { result, complexity });
    combinationMap.set(`${b},${a}`, { result, complexity });
    elementsByName.set(result, { id: result, name: result, complexity });
    const recipes = recipesByResult.get(result) ?? [];
    recipes.push([a, b]);
    recipesByResult.set(result, recipes);
  }
}
buildMaps();

/** Retourne les paires [A, B] qui donnent l’élément `result`. */
export function getRecipes(result: string): [string, string][] {
  return recipesByResult.get(result) ?? [];
}

// Ordre : d'abord les éléments de base (ordre BASE_ELEMENTS), puis les résultats dans l'ordre des combinaisons
const baseOrdered = BASE_ELEMENTS.map((name) => elementsByName.get(name)!);
const seen = new Set<string>(BASE_ELEMENTS);
const combinationOrdered: ElementInfo[] = [];
for (const [, , result] of COMBINATIONS) {
  if (!seen.has(result)) {
    seen.add(result);
    const info = elementsByName.get(result);
    if (info) combinationOrdered.push(info);
  }
}
const ELEMENTS_ORDERED = [...baseOrdered, ...combinationOrdered];

export function getCombination(a: string, b: string): CombinationResult | null {
  if (!a || !b) return null;
  return combinationMap.get(`${a},${b}`) ?? null;
}

export function getElementInfo(name: string): ElementInfo | undefined {
  return elementsByName.get(name);
}

export function getAllElements(): ElementInfo[] {
  return [...ELEMENTS_ORDERED];
}

export const ELEMENTS_IMAGE_BASE = '/elements/';

export function getElementImageIndex(name: string): number {
  const i = ELEMENTS_ORDERED.findIndex((e) => e.name === name);
  return i >= 0 ? i + 1 : 0;
}

export function getElementsImageMapping(): { index: number; name: string }[] {
  return ELEMENTS_ORDERED.map((e, i) => ({ index: i + 1, name: e.name }));
}

export function getRandomBaseElement(): string {
  return BASE_ELEMENTS[Math.floor(Math.random() * BASE_ELEMENTS.length)];
}

const ELEMENT_COLORS: Record<string, string> = {
  Terre: '#8B4513',
  Eau: '#1E90FF',
  Air: '#87CEEB',
  Feu: '#FF4500',
  Vie: '#32CD32',
  Métal: '#708090',
  Roche: '#696969',
  Océan: '#000080',
  Vent: '#B0E0E6',
  Brasier: '#FF6347',
  ADN: '#9370DB',
  Engrenage: '#2F4F4F',
  Boue: '#654321',
  Poussière: '#DEB887',
  Lave: '#DC143C',
  Graine: '#6B8E23',
  Minerai: '#4A4A4A',
  Nuage: '#F5F5F5',
  Vapeur: '#E6E6FA',
  Cellule: '#98FB98',
  Rouille: '#B8860B',
  Fumée: '#778899',
  Spore: '#DDA0DD',
  Étincelle: '#FFD700',
  Cendre: '#A9A9A9',
  Lingot: '#C0C0C0',
  Prothèse: '#8B7355',
  Sable: '#F4A460',
  Galets: '#BC8F8F',
  Argile: '#CD853F',
  Céramique: '#D2691E',
  Jarre: '#A0522D',
  Pluie: '#4682B4',
  Tempête: '#4169E1',
  Orage: '#483D8B',
  Brume: '#D3D3D3',
  Brouillard: '#A9A9A9',
  Marais: '#556B2F',
  Île: '#8FBC8F',
  Vagues: '#00CED1',
  Obsidienne: '#2F2F2F',
  Volcan: '#8B0000',
  Montagne: '#808080',
  Froid: '#E0FFFF',
  Glace: '#ADD8E6',
  Neige: '#FFFAFA',
  Blizzard: '#F0F8FF',
  Colonie: '#90EE90',
  Organisme: '#3CB371',
  Animal: '#CD5C5C',
  Poisson: '#20B2AA',
  Champignon: '#8B4513',
  Mammifère: '#A0522D',
  Amphibien: '#2E8B57',
  Humain: '#FFB6C1',
  Plante: '#228B22',
  Arbre: '#006400',
  Fleur: '#FF69B4',
  Four: '#4A4A4A',
  Forge: '#363636',
  'Outil métallique': '#5C5C5C',
  Atelier: '#696969',
  Outil: '#808080',
  Bûche: '#8B4513',
  'Feu de camp': '#FF8C00',
  Cuisine: '#FFA07A',
  'Poisson grillé': '#D2691E',
  Infusion: '#D2B48C',
  Ragoût: '#CD853F',
  Planche: '#DEB887',
  Cabane: '#8B4513',
  Village: '#A0522D',
  Verre: '#E0FFFF',
  Fenêtre: '#87CEEB',
  Maison: '#D2691E',
  Alliage: '#C0C0C0',
  Acier: '#71797E',
  "Hache d'acier": '#4A4A4A',
  Machine: '#708090',
  Moteur: '#2F4F4F',
  Éolienne: '#B0C4DE',
  Pompe: '#4682B4',
  Irrigation: '#5F9EA0',
  Agriculture: '#6B8E23',
  Marché: '#FFD700',
  Monnaie: '#FFDF00',
  Commerce: '#DAA520',
  Ville: '#CD853F',
  Générateur: '#4A4A4A',
  Électricité: '#FFEB3B',
  Ordinateur: '#36454F',
  Scientifique: '#9370DB',
  Laboratoire: '#E6E6FA',
  Couple: '#FFB6C1',
  Reproduction: '#FF69B4',
  Spermatozoïde: '#87CEEB',
  Ovule: '#FFB6C1',
  'Cellule-œuf': '#FFE4E1',
  Embryon: '#FFC0CB',
  Bébé: '#FFB6C1',
  Famille: '#FF69B4',
  Communauté: '#DDA0DD',
};

export function getElementColor(name: string): string {
  const hue = name.split('').reduce((a, b) => a + b.charCodeAt(0), 0) % 360;
  return ELEMENT_COLORS[name] ?? `hsl(${hue}, 60%, 50%)`;
}
